import React, {ChangeEvent, useState} from 'react';
import {useIntl} from 'react-intl';
import toast from 'react-hot-toast';
import {CommonFile, renameFile} from '../admin/documents/api';
import {Form, InputGroup, Modal} from 'react-bootstrap';
import RenameIcon from '../../common/components/RenameIcon';
import ButtonSubmit from '../../modules/form/ButtonSubmit';
import ButtonCancel from '../../modules/form/ButtonCancel';
import FileInputControlled from '../../modules/form/FileInputControlled';

interface RenameFileProps {
    file: CommonFile;
    onSuccess?: () => void;
}

const LANGUE_OPTIONS = ['fr', 'en', 'de', 'es', 'it'] as const;

const stripExtension = (fileName: string): string => {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
};

const getExtension = (fileName: string): string => {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.slice(lastDotIndex) : '';
};

const RenameFile = ({file, onSuccess}: RenameFileProps): JSX.Element => {
    const intl = useIntl();
    const [showModal, setShowModal] = useState(false);
    const [newFileName, setNewFileName] = useState(stripExtension(file.fileName));
    const [langue, setLangue] = useState((file.langue as unknown as string) || 'fr');
    const [fileNameError, setFileNameError] = useState('');
    const [newFile, setNewFile] = useState<File | undefined>(undefined);
    const [fileError, setFileError] = useState('');
    const fileExtension = getExtension(file.fileName);

    const handleFileChange = (event: ChangeEvent) => {
        const target = event.target as HTMLInputElement;
        const selectedFile = target.files?.[0];
        if (!selectedFile) {
            setNewFile(undefined);
            setFileError('');
            return;
        }
        if (getExtension(selectedFile.name).toLowerCase() !== fileExtension.toLowerCase()) {
            setNewFile(undefined);
            setFileError(
                intl.formatMessage(
                    {id: 'ADMIN.DOCUMENTS.TABLE.RENAME.FILE_EXTENSION_MISMATCH'},
                    {extension: fileExtension}
                )
            );
            return;
        }
        setNewFile(selectedFile);
        setFileError('');
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!newFileName.trim()) {
            setFileNameError(intl.formatMessage({id: 'FIELD.REQUIRED'}));
            return;
        }
        if (fileError) {
            return;
        }
        try {
            await renameFile(file.id, newFileName, langue, newFile);
            toast.success(intl.formatMessage({id: 'ADMIN.DOCUMENTS.TABLE.RENAME.SUCCESS'}));
            setShowModal(false);
            if (onSuccess) {
                onSuccess();
            }
            window.location.reload();
        } catch (error) {
            toast.error(intl.formatMessage({id: 'ADMIN.DOCUMENTS.TABLE.RENAME.FAIL'}));
        }
    };

    return (
        <>
            <RenameIcon
                onClick={() => setShowModal(true)}
                tooltip={intl.formatMessage({id: 'ADMIN.DOCUMENTS.TABLE.RENAME'})}
            />
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Form noValidate onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {intl.formatMessage({id: 'ADMIN.DOCUMENTS.TABLE.RENAME.TITLE'})}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group controlId="newFileName">
                            <Form.Label>
                                {intl.formatMessage({id: 'ADMIN.DOCUMENTS.TABLE.RENAME.NEW_NAME'})}
                            </Form.Label>
                            <InputGroup hasValidation>
                                <Form.Control
                                    type="text"
                                    value={newFileName}
                                    onChange={(event) => {
                                        setNewFileName(event.target.value);
                                        if (fileNameError) {
                                            setFileNameError('');
                                        }
                                    }}
                                    isInvalid={!!fileNameError}
                                    required
                                />
                                {fileExtension && (
                                    <InputGroup.Text>{fileExtension}</InputGroup.Text>
                                )}
                                <Form.Control.Feedback type="invalid">
                                    {fileNameError}
                                </Form.Control.Feedback>
                            </InputGroup>
                        </Form.Group>
                        <Form.Group controlId="langue" className="mt-4">
                            <Form.Label>
                                {intl.formatMessage({id: 'ADMIN.DOCUMENTS.SELECT.LANGUE'})}
                            </Form.Label>
                            <Form.Select
                                value={langue}
                                onChange={(event) => setLangue(event.target.value)}
                            >
                                {LANGUE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                        {intl.formatMessage({
                                            id: `DOCUMENTS.LANGUES.${option.toUpperCase()}`,
                                        })}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <div className="mt-4">
                            <FileInputControlled
                                name="replaceFile"
                                label={intl.formatMessage({
                                    id: 'ADMIN.DOCUMENTS.TABLE.RENAME.REPLACE_FILE',
                                })}
                                value={newFile}
                                onChange={handleFileChange}
                                onReset={() => {
                                    setNewFile(undefined);
                                    setFileError('');
                                }}
                                touched={!!fileError}
                                errors={fileError}
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <ButtonCancel onClick={() => setShowModal(false)} textId="CANCEL"/>
                        <ButtonSubmit textId="SAVE" disabled={false} isLoading={false}/>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default RenameFile;
